import { DatePipe } from "@angular/common";
import { Injectable } from "@angular/core";
import { ActivityListService } from './activity-list.service';

import { HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { of } from 'rxjs/observable/of';
import { map, switchMap } from 'rxjs/operators';
import { HttpClientService } from "../Services/http-client.service";
import { AppSettings } from "../app-settings";
import { SessionService } from "./session.service";

@Injectable()
export class JourneyMilestoneService {
  journeyData: any;

  gradeClassConfig = {
    A: "grade_A",
    B: "grade_B",
    'A-': "grade_A_Minus",
    'B-': "grade_B_Minus",
    C: "grade_C",
    NR: "grade_NR",
    NA: "grade_NA",
    NG: "grade_NA"
  };

  startStates = {
    'IN SEQUENCE': 'In Seq.',
    'OUT OF SEQUENCE': 'Out of Seq.',
    'UNAVAILABLE': 'Unavailable'
  }


  constructor(
    private httpClientService: HttpClientService,
    private datePipe: DatePipe,
    private activityService: ActivityListService,
    private sessionService:SessionService
  ) {}

  getData(journeyCourseID) {
    let url = AppSettings.restApiUrl + "/api/journey/" + journeyCourseID;
    let headers = new HttpHeaders();
    return this.httpClientService.get(url, headers).pipe(switchMap((response => {
      this.sessionService.shareSectionName(response['section']);
      return of(this.formatData(response));
    })))

  }

  /**
   * Journey name (subject short name, e.g. CR) and section of a journey course.
   * Used by the module view, whose course payload carries neither.
   */
  getJourneyIdentity(journeyCourseID): Observable<{ journeyName: string; section: string }> {
    let url = AppSettings.restApiUrl + "/api/journey/" + journeyCourseID;
    let headers = new HttpHeaders();
    return this.httpClientService.get(url, headers).pipe(
      map(response => ({ journeyName: response ? response['journeyName'] : null, section: response ? response['section'] : null }))
    );
  }

  formatData(journeyRawData) {
    let journeyData = {};
    for (let i = 1; i < journeyRawData["modules"].length; i++) {
      if (journeyRawData["modules"][i - 1]["levelStatus"] !== "COMPLETED") {
        journeyRawData["modules"][i]["isPreviousLevelCompleted"] = false;
      } else {
        journeyRawData["modules"][i]["isPreviousLevelCompleted"] = true;
      }
    }
    journeyRawData["modules"][0]["isPreviousLevelCompleted"] = true;
    journeyData["courseID"] = journeyRawData["journeyCourseId"];
    journeyData["courseStage"] = journeyRawData["currentCourseStage"];
    journeyData["courseShortName"] = journeyRawData["journeyName"];
    journeyData["section"] = journeyRawData["section"];

    journeyData["learningLevels"] = [];

    journeyRawData["modules"].forEach((module) => {
      let learningLevel = {};
      if (module["isRecentModule"] === "YES" && (!['CM','CH'].includes(module["shortName"]))) {
        learningLevel["collapsed"] = false;
      } else {
        learningLevel["collapsed"] = true;
      }
      learningLevel["activityDetailsCollapsed"] = true;
      learningLevel["isPreviousLevelCompleted"] =
        module["isPreviousLevelCompleted"];
      learningLevel["levelName"] = module["displayName"];
      learningLevel["shortName"] = module["shortName"];
      learningLevel["levelStatus"] = this.CapitalizeString(module["levelStatus"].replace(/_/g, " "));
      learningLevel["startState"] = this.CapitalizeString(module["startState"].replace(/_/g, " "));

      learningLevel["grade"] = module["grade"];
      learningLevel["id"] = module["id"];
      learningLevel["gradeClass"] = this.gradeClassConfig[module["grade"]];
      learningLevel["completedActivities"] = module["completedActivities"];
      learningLevel["totalActivities"] = module["totalActivities"];

      learningLevel["totalTimeSpent"] = module["timeSpentInMinutes"];
      learningLevel["remainingEffort"] = module["remaingTimeInMinutes"];

      learningLevel["nextActivityName"] = module["nextActivityName"];
      learningLevel["nextActivityID"] = module["nextActivityId"];
      learningLevel["isRecentModule"] = module["isRecentModule"];

      learningLevel["overallPerformanceScore"] = module["overallPerformance"];

      learningLevel["activityListInfo"] = this.formatActivityDetails(
        module["activities"]
      );

      journeyData["learningLevels"].push(learningLevel);
    });

    return journeyData;
  }

  formatActivityDetails(activitiesRawData) {
    let activities = [];

    activitiesRawData.forEach((rawActivity) => {
      let activity = {};
      activity["name"] = rawActivity["activityName"];
      activity["id"] = rawActivity["id"];
      activity["firstAttemptScore"] = rawActivity["firstAttemptScorePercent"] !== -1 ? rawActivity["firstAttemptScorePercent"] + "%" : "NA";
      activity["numberOfAttempts"] = rawActivity["totalAttempts"];
      activity["attempts"] = rawActivity["attempts"]
        ? rawActivity["attempts"]
        : [];
      activity["attempts"] = this.getDateTime(activity["attempts"]);
      activity["startState"] = rawActivity["startState"]
        ? this.startStates[rawActivity["startState"].replace(/_/g, " ")]
        : "NA";
      activity["status"] = rawActivity["userState"].includes("COMPLETED")
        ? "COMPLETED"
        : rawActivity["userState"];
      activity["median"] = rawActivity["median"];
      activity["grade"] =
        rawActivity["grade"] === "NR"
          ? "NR"
          : rawActivity["grade"] === "NG"
          ? "NG"
          : rawActivity["grade"] === "NA"
          ? "NA" : "Grade " + rawActivity["grade"];
      activity["isGraded"] = rawActivity["graded"];
      activity["grade_text"] = rawActivity["grade"];
      activity["recommendedTime"] = rawActivity["recommendedCompeltionTimeInMins"];
      activity["showRecommendedTime"] = false;
      activity["timeSpentClicked"] = false;
      activity["gradeClass"] = this.gradeClassConfig[rawActivity["grade"]];
      if (rawActivity["isRecent"] === "YES") {
        activity["detailsCollapsed"] = false;
      } else {
        activity["detailsCollapsed"] = true;
      }

      // start

      activity["type"] = rawActivity["type"];
      activity["isRecent"] = rawActivity["isRecent"];
      activity["isValidAccess"] = rawActivity["hasAccess"];
      activity["paceAction"] = rawActivity["paceAction"];
      activity["isArchived"] = rawActivity["archived"];
      activity["quizType"] = rawActivity["quizType"];
      activity["scorePercentage"] = rawActivity["userScore"];
      activity["timeSpent"] = rawActivity["timeSpent"];
      activity["statusImage"] =
        AppSettings.cdnUrl +
        this.activityService.activityStatus[rawActivity["userState"]].Image;
      activity["minutes"] = Math.floor(rawActivity["timeSpent"] / 60);
      if (rawActivity["isRecent"] === "YES") {
        activity["activityColor"] = "#00aeef";
      } else {
        activity["activityColor"] = "rgb(127, 127, 127)";
      }
      if (activity["type"] === "HTML5") {
        if (activity["isRecent"] === "YES") {
          activity["imageIcon"] =
            AppSettings.cdnUrl + "assets/images/html5_icon_blue.png";
        } else {
          activity["imageIcon"] =
            AppSettings.cdnUrl + "assets/images/html5_icon_grey.png";
        }
      } else if (activity["type"] === "MIQA") {
        if (activity["isRecent"] === "YES") {
          activity["imageIcon"] =
            AppSettings.cdnUrl + "assets/images/miqa_quiz_blue.png";
        } else {
          activity["imageIcon"] =
            AppSettings.cdnUrl + "assets/images/miqa_quiz_grey.png";
        }
      } else if (
        activity["type"] === "RISE_ARTICLE" ||
        activity["type"] === "RISE_TINCAN"
      ) {
        if (activity["isRecent"] === "YES") {
          activity["imageIcon"] =
            AppSettings.cdnUrl + "assets/images/rise_blue.png";
        } else {
          activity["imageIcon"] =
            AppSettings.cdnUrl + "assets/images/rise_grey.png";
        }
      } else if (activity["type"] === "RISE_ADOBE_CONNECT") {
        if (activity["isRecent"] === "YES") {
          activity["imageIcon"] =
            AppSettings.cdnUrl + "assets/images/video-camera.png";
        } else {
          activity["imageIcon"] =
            AppSettings.cdnUrl + "assets/images/video-camera.png";
        }
      }
      //end
      activities.push(activity);
    });

    return activities;
  }

  getDateTime(attempts) {
    if (attempts) {
      let updatedAttempts = [];
      attempts.forEach((attempt) => {
        attempt.timestamp = this.datePipe.transform(
          new Date(attempt.timestamp),
          "MMM d, y, h:mm a"
        );
        updatedAttempts.push(attempt);
      });
      return updatedAttempts;
    } else {
      return [];
    }
  }

  CapitalizeString(str) {
    let levelStatus = [];
    let capitalizedLevelStatus = [];
    // let levelStartState = [];
    // let capitalizedlevelStartState = [];
    levelStatus = str.toLowerCase().split(" ");
    levelStatus.forEach((element) => {
      capitalizedLevelStatus.push(
        element[0].toUpperCase() + element.slice(1, element.length)
      );
    });
    return capitalizedLevelStatus.join(" ");
    // levelStartState.forEach((element) => {
    //   capitalizedlevelStartState.push(
    //     element[0].toUpperCase() + element.slice(1, element.length)
    //   );
    // });
    // this.level.startState = capitalizedlevelStartState.join(" ");
  }

}
